<script setup lang="ts">
import { useDisplay } from 'vuetify/framework'

import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import ShortFio from '@/components/createdUi/ShortFio.vue'

import type { IAuditCardAssignmentRow } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditCardAssignmentRow'

const props = defineProps<{
  auditId: number
  departmentId: string
  isManager: boolean
}>()

const emit = defineEmits<{
  (e: 'row-click', row: IAuditCardAssignmentRow): void
  (e: 'auditor-click', auditorId: string): void
}>()

const { smAndDown } = useDisplay()
const apiStore = useApiStore()
const auditRegisterStore = useAuditRegisterStore()

const {
  auditCardAssignments,
  auditCardAssignmentsLoading,
  auditCardAssignmentsSort,
  auditCardNonCompliancesCount,
  auditCardCompliancesCount,
  auditCardNotApplicableCount,
} = storeToRefs(auditRegisterStore)

const assignmentStats = computed(() => [
  {
    label: 'Несоответствий',
    count: auditCardNonCompliancesCount.value,
    color: 'error',
  },
  {
    label: 'Соответствий',
    count: auditCardCompliancesCount.value,
    color: 'success',
  },
  {
    label: 'Не применимо',
    count: auditCardNotApplicableCount.value,
    color: 'secondary',
  },
])

/* Контекст до useTableFilters — иначе loadFiltersFromQuery не подтянет choices */
if (props.auditId && props.departmentId) {
  auditRegisterStore.setAuditCardAssignmentContext(props.auditId, props.departmentId)
}

/* Обязательные поля useTableFilters; пагинации на карточке нет */
const itemsPerPage = ref(1)
const nextCursor = ref<string | null>(null)

function getStatusLabel(status: string) {
  if (!status) return 'Ожидает'
  const statuses = apiStore.responsibilityEntriesStatuses
  return (statuses as Record<string, string>)[status] || status
}

const filterConfigs: IFilterConfig[] = [
  { key: 'parent_numbers', labelPrefix: '№: ', type: 'tree' },
  { key: 'parent_names', labelPrefix: 'Предмет проверки: ', type: 'text' },
  { key: 'auditors', labelPrefix: 'Аудитор: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: getStatusLabel },
]

interface IHeaderColumn {
  title: string
  key: string
  sortable?: boolean
  filterType?: 'tree' | 'text' | 'status'
  filterKey: 'parent_numbers' | 'parent_names' | 'auditors' | 'status' | null
}

const headerColumns: IHeaderColumn[] = [
  { title: '№', key: 'parent_number', filterType: 'tree', filterKey: 'parent_numbers' },
  { title: 'Предмет проверки', key: 'parent_name', filterType: 'text', filterKey: 'parent_names' },
  { title: 'Аудитор', key: 'auditor', filterType: 'tree', filterKey: 'auditors' },
  { title: 'Статус', key: 'status', filterType: 'status', filterKey: 'status' },
  { title: 'Результат', key: 'result', sortable: false, filterKey: null },
]

/* Ключи колонок совпадают с полями API сортировки (?sort=parent_name) */
const sortMap: Record<string, string> = {
  parent_number: 'parent_number',
  parent_name: 'parent_name',
  auditor: 'auditor',
  status: 'status',
}

/* UniversalFilterTree ждёт filterChoices/fetchFilterChoices — в сторе префикс auditCard* */
const cardFilterStore = {
  get filterChoices() {
    return auditRegisterStore.auditCardAssignmentFilterChoices
  },
  get searchChoices() {
    return auditRegisterStore.auditCardAssignmentSearchChoices
  },
  get filterCursors() {
    return auditRegisterStore.auditCardAssignmentFilterCursors
  },
  get filterLoading() {
    return auditRegisterStore.auditCardAssignmentFilterLoading
  },
  get filterStrategies() {
    return auditRegisterStore.auditCardAssignmentFilterStrategies
  },
  fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
    return auditRegisterStore.fetchAuditCardAssignmentFilterChoices(fieldKey, search, cursor)
  },
}

const tableLogic = useTableFilters({
  filters: computed(() => auditRegisterStore.auditCardAssignmentFilters),
  searchChoices: computed(() => auditRegisterStore.auditCardAssignmentSearchChoices) as any,
  filterChoices: computed(() => auditRegisterStore.auditCardAssignmentFilterChoices) as any,
  filterLoading: auditRegisterStore.auditCardAssignmentFilterLoading,
  fetchFilterChoices: (key: string) => auditRegisterStore.fetchAuditCardAssignmentFilterChoices(key),
  fetchData: () => auditRegisterStore.fetchAuditCardAssignments(),
  currentSortString: auditCardAssignmentsSort,
  itemsPerPage,
  resetFilters: () => auditRegisterStore.resetAuditCardAssignmentFilters(),
  nextCursor,
  loadedItemsCount: computed(() => auditCardAssignments.value.length),
  totalItemsCount: computed(() => auditCardAssignments.value.length),
  sortMap,
  filterConfigs,
  resetOnUnmount: false,
})

/* Таблица собрана вручную, поэтому цикл asc → desc → без сортировки ведём сами */
const toggleSort = (colKey: string) => {
  const backendKey = sortMap[colKey]
  if (!backendKey) return

  const current = auditCardAssignmentsSort.value

  const nextOrder = current === backendKey
    ? 'desc'
    : current === `-${backendKey}` ? null : 'asc'

  tableLogic.handlePageChange({
    page: 1,
    itemsPerPage: itemsPerPage.value,
    sortBy: nextOrder ? [{ key: colKey, order: nextOrder }] : [],
  })
}

const openParentNamesFilter = () => {
  tableLogic.tempValues.value.parent_names
    = auditRegisterStore.auditCardAssignmentFilters.parent_names || ''
  tableLogic.handleFilterClick('parent_names')
}

const applyParentNamesFilter = () => {
  const inputStr = String(tableLogic.tempValues.value.parent_names || '').trim()
  auditRegisterStore.auditCardAssignmentFilters.parent_names = inputStr
  tableLogic.filterMenus.value.parent_names = false
  tableLogic.triggerServerFilter()
}

const loadForDepartment = async (isDepartmentSwitch: boolean) => {
  if (!props.auditId || !props.departmentId) return
  auditRegisterStore.setAuditCardAssignmentContext(props.auditId, props.departmentId)

  /* F5: фильтры уже из query. Сброс только при смене отделения. */
  if (isDepartmentSwitch) {
    auditRegisterStore.resetAuditCardAssignmentFilters()
  }

  await auditRegisterStore.fetchAuditCardAssignments()
}

watch(
  () => [props.auditId, props.departmentId] as const,
  (_next, prev) => {
    const isDepartmentSwitch = Boolean(prev && prev[1] !== props.departmentId)
    void loadForDepartment(isDepartmentSwitch)
  },
  { immediate: true },
)

const onRowClick = (row: IAuditCardAssignmentRow) => {
  if (!props.isManager) return

  emit('row-click', row)
}
</script>

<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex align-center ga-6 flex-wrap">
      <span
        v-for="item in assignmentStats"
        :key="item.label"
        class="d-flex align-center ga-2"
      >
        <VIcon
          icon="ri-circle-fill"
          size="8"
          :color="item.color"
        />
        {{ item.label }} - {{ item.count }}
      </span>
    </div>

    <div
      v-if="tableLogic.activeFilterChips.value.length"
      class="filter-chip d-flex align-center ga-4"
    >
      <VChip
        v-for="chip in tableLogic.activeFilterChips.value"
        :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
        closable
        @click:close="tableLogic.removeFilter(chip)"
      >
        {{ chip.label }}
      </VChip>

      <VBtn
        class="ml-auto flex-shrink-0"
        :class="{ 'w-100': smAndDown }"
        prepend-icon="ri-delete-bin-line"
        variant="outlined"
        color="secondary"
        :loading="auditCardAssignmentsLoading"
        @click="tableLogic.renewDataAndResetFilters"
      >
        Сбросить фильтры
      </VBtn>
    </div>

    <div class="overflow-x-auto position-relative">
      <VProgressLinear
        v-if="auditCardAssignmentsLoading"
        indeterminate
        color="primary"
        class="position-absolute w-100"
        style="z-index: 1"
      />

      <table class="responsibility-table w-100">
        <thead>
          <tr>
            <th v-for="col in headerColumns" :key="col.title">
              <div class="d-flex align-center">
                <span>{{ col.title }}</span>

                <VIcon
                  v-if="col.sortable !== false"
                  size="16"
                  class="ml-1 custom-sort-icon"
                  :icon="tableLogic.getSortIcon(col.key)"
                  :class="{
                    'is-active': tableLogic.isSortActive(col.key),
                    'is-desc': tableLogic.isSortDesc(col.key),
                  }"
                  @click.stop="toggleSort(col.key)"
                />

                <template v-if="col.filterKey">
                  <div
                    v-if="tableLogic.filterLoading?.[col.filterKey]"
                    class="d-flex align-center justify-center ml-1"
                    style="width: 28px; height: 28px"
                  >
                    <VProgressCircular
                      indeterminate
                      color="primary"
                      size="20"
                      width="2"
                    />
                  </div>

                  <VIconBtn
                    v-else
                    variant="plain"
                    size="small"
                    class="filter-icon-btn"
                    :icon="tableLogic.getFilterIcon(col.filterKey)"
                    @click.stop="col.filterType === 'text'
                      ? openParentNamesFilter()
                      : tableLogic.handleFilterClick(col.filterKey)"
                  />

                  <VMenu
                    v-if="col.filterType === 'text'"
                    v-model="tableLogic.filterMenus.value[col.filterKey]"
                    :close-on-content-click="false"
                    location="bottom"
                    transition="scale-transition"
                  >
                    <template #activator="{ props: menuProps }">
                      <div v-bind="menuProps" />
                    </template>
                    <VCard class="pa-4">
                      <VTextField
                        v-model="tableLogic.tempValues.value[col.filterKey]"
                        autofocus
                        placeholder="Поиск"
                        hide-details
                        width="220px"
                        @keyup.enter="applyParentNamesFilter"
                      />
                      <div class="d-flex justify-end mt-2">
                        <VBtn
                          :class="{ 'w-100': smAndDown }"
                          size="small"
                          variant="outlined"
                          color="secondary"
                          @click="applyParentNamesFilter"
                        >
                          Применить
                        </VBtn>
                      </div>
                    </VCard>
                  </VMenu>

                  <VMenu
                    v-else
                    v-model="tableLogic.filterMenus.value[col.filterKey]"
                    :close-on-content-click="false"
                    location="bottom start"
                    transition="scale-transition"
                  >
                    <template #activator="{ props: menuProps }">
                      <div v-bind="menuProps" />
                    </template>
                    <UniversalFilterTree
                      :field-key="col.filterKey"
                      :store="cardFilterStore"
                      :model-value="tableLogic.tempSelectedFilters.value[col.filterKey] || []"
                      :placeholder="`Поиск: ${col.title}`"
                      @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                      @apply="tableLogic.applyFilter(col.filterKey!)"
                    />
                  </VMenu>
                </template>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!auditCardAssignmentsLoading && !auditCardAssignments.length">
            <td :colspan="headerColumns.length" class="text-center text-italic">
              Данные отсутствуют
            </td>
          </tr>

          <tr
            v-for="row in auditCardAssignments"
            :key="row.assignment_id"
            :class="{ 'cursor-pointer': isManager }"
            @click="onRowClick(row)"
          >
            <td>{{ row.parent_number }}</td>
            <td>{{ row.parent_name }}</td>
            <td>
              <ShortFio
                class="text-primary cursor-pointer"
                :person="row.auditor"
                @click.stop="emit('auditor-click', row.auditor.id)"
              />
            </td>
            <td class="status-cell">
              <VChip
                class="status-badge"
                :class="row.status"
                size="small"
              >
                {{ getStatusLabel(row.status) }}
              </VChip>
            </td>
            <td>
              <div class="d-flex flex-column ga-1">
                <span>Пройдено: {{ row.completed_points }}/{{ row.total_points }}</span>
                <span v-if="row.non_compliances > 0" class="text-error">
                  Несоответствий: {{ row.non_compliances }}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped lang="scss">
.responsibility-table {
  width: 100%;
  border-collapse: collapse;
}

.responsibility-table thead th {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity));
  position: relative;
  white-space: nowrap;
  text-align: left;
  padding-block: 12px;
  padding-inline: 12px;
  font-weight: 500;
}

.responsibility-table thead th:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 1px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

.responsibility-table tbody td {
  padding-block: 12px;
  padding-inline: 12px;
  vertical-align: top;
  white-space: nowrap;
}

.responsibility-table thead th:nth-child(1),
.responsibility-table tbody td:nth-child(1) {
  min-width: 4rem;
}

.responsibility-table thead th:nth-child(2),
.responsibility-table tbody td:nth-child(2) {
  min-width: 18rem;
  white-space: normal;
}

.responsibility-table thead th:nth-child(3),
.responsibility-table tbody td:nth-child(3) {
  min-width: 14rem;
}

.responsibility-table thead th:nth-child(4),
.responsibility-table tbody td:nth-child(4) {
  min-width: 8rem;
}

.responsibility-table thead th:nth-child(5),
.responsibility-table tbody td:nth-child(5) {
  min-width: 12rem;
}

.custom-sort-icon {
  display: inline-block;
  cursor: pointer;
  opacity: 0.15;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  vertical-align: middle;

  &:hover {
    opacity: 0.6;
  }

  &.is-active {
    color: rgb(var(--v-theme-primary)) !important;
    opacity: 1 !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

.status-badge {
  font-weight: 500;
  padding-inline: 6px;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}
</style>
